/**
 * Pure assignment reconciliation for budget revisions.
 *
 * A budget line must never retain more assigned hours than its revised sold
 * hours. When a line is reduced, existing assignments are scaled
 * proportionally and shown to the reviser before commit.
 */

export interface RevisionAssignmentInput {
  id: string;
  userId: string;
  userName: string;
  hours: number;
  taskStatus: string;
}

export interface RevisionAssignmentAdjustment {
  assignmentId: string;
  budgetLineId: string;
  lineDescription: string;
  userId: string;
  userName: string;
  taskStatus: string;
  beforeHours: number;
  afterHours: number;
  action: "update" | "delete";
}

const HUNDRED = 100;
const EPSILON = 0.0001;

const toHundredths = (hours: number) => Math.round(hours * HUNDRED);

/**
 * Returns only the assignment rows that must change.
 *
 * Reductions across multiple people keep the existing relative split. Values
 * are rounded to hundredths, with the rounding remainder distributed by the
 * largest fractional share so the result always sums exactly to targetHours.
 */
export function planAssignmentAdjustments(
  budgetLineId: string,
  assignments: RevisionAssignmentInput[],
  targetHours: number,
  lineDescription = "Oppgave",
): RevisionAssignmentAdjustment[] {
  const active = assignments.filter((assignment) => assignment.hours > 0);
  const total = active.reduce((sum, assignment) => sum + assignment.hours, 0);
  const target = Math.max(0, targetHours);

  if (active.length === 0 || total <= target + EPSILON) return [];

  if (target <= EPSILON) {
    return active.map((assignment) => ({
      assignmentId: assignment.id,
      budgetLineId,
      lineDescription,
      userId: assignment.userId,
      userName: assignment.userName,
      taskStatus: assignment.taskStatus,
      beforeHours: assignment.hours,
      afterHours: 0,
      action: "delete" as const,
    }));
  }

  const targetUnits = toHundredths(target);
  const scaled = active.map((assignment) => {
    const rawUnits = (assignment.hours / total) * targetUnits;
    const floorUnits = Math.floor(rawUnits);
    return { assignment, rawUnits, units: floorUnits };
  });

  let remainder = targetUnits - scaled.reduce((sum, row) => sum + row.units, 0);
  const remainderOrder = [...scaled].sort((a, b) => {
    const fractionDiff = (b.rawUnits - b.units) - (a.rawUnits - a.units);
    return fractionDiff !== 0
      ? fractionDiff
      : a.assignment.id.localeCompare(b.assignment.id);
  });
  for (let index = 0; remainder > 0; index += 1, remainder -= 1) {
    remainderOrder[index % remainderOrder.length].units += 1;
  }

  return scaled
    .map(({ assignment, units }) => {
      const afterHours = units / HUNDRED;
      return {
        assignmentId: assignment.id,
        budgetLineId,
        lineDescription,
        userId: assignment.userId,
        userName: assignment.userName,
        taskStatus: assignment.taskStatus,
        beforeHours: assignment.hours,
        afterHours,
        action: afterHours <= EPSILON ? "delete" as const : "update" as const,
      };
    })
    .filter((adjustment) => Math.abs(adjustment.beforeHours - adjustment.afterHours) > EPSILON);
}
